<template>
  <section class="about-hero" aria-label="hero">
    <!-- Floating ambient shapes -->
    <div class="hero-shape hero-shape--blue"  aria-hidden="true"></div>
    <div class="hero-shape hero-shape--red"   aria-hidden="true"></div>
    <div class="hero-shape hero-shape--purple" aria-hidden="true"></div>

    <div class="hero-inner">
      <!-- Eyebrow -->
      <div class="hero-eyebrow">
        <span class="eyebrow-dot" aria-hidden="true"></span>
        <span>{{ $t('about.about') }}</span>
      </div>

      <!-- Headline -->
      <h1 class="hero-headline">
        <span class="line line--plain">{{ $t('about.heroLine1') }}</span>
        <span class="line line--gradient">{{ $t('about.heroLine2') }}</span>
      </h1>

      <!-- Sub -->
      <p class="hero-sub">{{ $t('about.heroDes') }}</p>

      <!-- Scroll cue -->
      <div class="hero-scroll" aria-hidden="true">
        <div class="scroll-pill">
          <div class="scroll-dot"></div>
        </div>
        <span class="scroll-label">{{ $t('about.scrollDown') }}</span>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'HeroSection'
}
</script>

<style lang="scss" scoped>
// ── Brand palette ─────────────────────────────────────────────────────
$blue:   #3949ab;
$purple: #7c3aed;
$red:    #e53935;

// ── Section wrapper ───────────────────────────────────────────────────
// Vuetify v-main already offsets content below the fixed app-bar (~64px),
// so we subtract that to keep the hero visually flush with one viewport.
.about-hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #fafafa;

  .theme--dark & {
    background: #09090f;
  }
}

// ── Ambient blobs ─────────────────────────────────────────────────────
.hero-shape {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  filter: blur(100px);
  opacity: 0.13;

  &--blue {
    width: 720px;
    height: 720px;
    top: -240px;
    right: -180px;
    background: $blue;
    animation: floatA 14s ease-in-out infinite;
  }

  &--red {
    width: 520px;
    height: 520px;
    bottom: -160px;
    left: -140px;
    background: $red;
    animation: floatB 16s ease-in-out infinite;
  }

  &--purple {
    width: 380px;
    height: 380px;
    top: 55%;
    left: 55%;
    transform: translate(-50%, -50%);
    background: $purple;
    animation: floatC 11s ease-in-out infinite;
    opacity: 0.08;
  }
}

// ── Content container ─────────────────────────────────────────────────
.hero-inner {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 120px 24px 80px;
  max-width: 880px;
  margin: 0 auto;
}

// ── Eyebrow label ─────────────────────────────────────────────────────
.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: $blue;
  margin-bottom: 36px;
  opacity: 0;
  animation: fadeUp 0.8s 0.2s ease-out forwards;

  .theme--dark & {
    color: lighten($blue, 28%);
  }
}

.eyebrow-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, $blue, $red);
  flex-shrink: 0;
}

// ── Headline ──────────────────────────────────────────────────────────
.hero-headline {
  font-size: clamp(2.6rem, 7.5vw, 5.4rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.03em;
  margin: 0 0 32px;
  opacity: 0;
  animation: fadeUp 0.9s 0.35s ease-out forwards;

  // Arabic script needs more vertical breathing room and no negative tracking
  [dir='rtl'] & {
    line-height: 1.45;
    letter-spacing: 0;
  }

  .line {
    display: block;
  }

  .line--plain {
    color: #1a1a2e;

    .theme--dark & {
      color: #f0f0f0;
    }
  }

  .line--gradient {
    background: linear-gradient(130deg, $blue 0%, $purple 48%, $red 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}

// ── Subtitle ──────────────────────────────────────────────────────────
.hero-sub {
  font-size: clamp(1rem, 2.2vw, 1.22rem);
  line-height: 1.8;
  color: #5a5a72;
  max-width: 640px;
  margin: 0 auto 64px;
  opacity: 0;
  animation: fadeUp 0.9s 0.5s ease-out forwards;

  .theme--dark & {
    color: #9999b5;
  }
}

// ── Scroll indicator ──────────────────────────────────────────────────
.hero-scroll {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  opacity: 0;
  animation: fadeIn 1s 1.4s ease-out forwards;
}

.scroll-pill {
  width: 26px;
  height: 44px;
  border: 2px solid rgba($blue, 0.3);
  border-radius: 13px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 7px;
}

.scroll-dot {
  width: 5px;
  height: 9px;
  background: $blue;
  border-radius: 3px;
  animation: scrollDot 2.2s ease-in-out infinite;
}

.scroll-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #aaa;
}

// ── Keyframes ─────────────────────────────────────────────────────────
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@keyframes floatA {
  0%, 100% { transform: translate(0, 0); }
  50%       { transform: translate(-28px, 28px); }
}

@keyframes floatB {
  0%, 100% { transform: translate(0, 0); }
  50%       { transform: translate(22px, -22px); }
}

@keyframes floatC {
  0%, 100% { transform: translate(-50%, -50%); }
  50%       { transform: translate(-50%, -50%) scale(1.18); }
}

@keyframes scrollDot {
  0%   { opacity: 1; transform: translateY(0); }
  75%  { opacity: 0; transform: translateY(14px); }
  100% { opacity: 0; transform: translateY(14px); }
}

// ── Responsive ────────────────────────────────────────────────────────
@media (max-width: 768px) {
  .hero-inner {
    padding: 100px 20px 60px;
  }

  .hero-shape--blue   { width: 320px; height: 320px; top: -120px; right: -120px; }
  .hero-shape--red    { width: 260px; height: 260px; bottom: -90px; left: -90px; }
  .hero-shape--purple { width: 200px; height: 200px; }
}
</style>
